import { Observable } from 'rxjs';
import { AiAssistant, AiConnection, Action, NamespaceConfig } from './ai-assistant.interface';
import { AssistantType, VendorModel } from '@vendasta/protos/ai_assistants/v1alpha1';
import { PlatformLocation } from '../ai-assistant.constants';

export interface AssistantInfo {
  assistantId: string;
  iconUrl?: string;
  cardDisabled?: boolean;
  cta?: Action;
  hideCard?: boolean;
  chatEnabled?: boolean;
}

export interface AiAssistantConfig {
  // Current partner ID.
  partnerId$: Observable<string | undefined | null>;

  // Optional: Current market ID
  marketId$: Observable<string | undefined | null>;

  // Optional: Current account group ID.
  accountGroupId$: Observable<string | undefined | null>;

  // Namespace
  namespaceConfig$: Observable<NamespaceConfig | undefined | null>;

  // Optional: default ai assistant list.
  defaultAIWorkforce$: Observable<AiAssistant[] | undefined | null>;

  // Optional: default connection list.
  defaultConnections$: Observable<AiConnection[] | undefined | null>;

  // Optional: current user ID.
  currentUserId$: Observable<string | undefined | null>;

  // Optional: whether the voice AI feature is available.
  voiceAIAvailable$: Observable<boolean | undefined | null>;

  // Optional: whether to show custom assistants based on business rules (e.g., paid SKUs, subscriptions).
  showCustomAssistant$: Observable<boolean | undefined | null>;

  // Optional: current platform location.
  platformLocation: PlatformLocation;

  // Whether the AI Marketer is available.
  aiMarketerAvailable$: Observable<boolean>;

  // Optional: Static capability product map
  capabilityProductMap?: CapabilityProductMap;

  // Optional: Assistant product map for white-labeled products (Conversations AI, Reputation Management)
  assistantProductMap$?: Observable<AssistantProductMap>;

  // Optional: whether the CRM Update capability's more entities config (opportunities, custom objects) is enabled.
  crmUpdateMoreEntitiesEnabled$?: Observable<boolean>;

  // Optional: the business's primary operating language code (from the listing profile). Used to show
  // which language AI conversation summaries are written in on lead-capture capability cards.
  primaryOperatingLanguage$?: Observable<string | undefined | null>;

  // Optional: navigates to the business profile's primary operating language field. When provided,
  // lead-capture capability cards render a link to set/change the language used for AI conversation
  // summaries; when omitted, the cards show the same context as plain text.
  navigateToOperatingLanguage?: () => void;

  // Optional: per-field lock map for the assistant edit form. Each `true` renders that field/bundle
  // read-only; cosmetic fields (name, icon, description, triggers, capability config vars) are never
  // locked and have no entry. Leans editable: defaults to fully editable when omitted, and the caller
  // should keep it editable while loading or on error so a data hiccup never locks out a legitimate user.
  assistantFieldLocks$?: Observable<AssistantFieldLockMap>;
}

// Per-field lock map for the assistant edit form. `true` means the field/bundle is locked (read-only).
export interface AssistantFieldLockMap {
  prompt: boolean;
  model: boolean;
  voice: boolean;
  knowledge: boolean;
  capabilities: boolean;
  tools: boolean;
}

// The lean-editable default: nothing locked. Used when no lock map is provided.
export const NO_FIELD_LOCKS: AssistantFieldLockMap = {
  prompt: false,
  model: false,
  voice: false,
  knowledge: false,
  capabilities: false,
  tools: false,
};

export enum VoiceLanguage {
  ENGLISH = 'English',
  ITALIAN = 'Italian',
  SPANISH = 'Spanish',
  FRENCH = 'French',
  DUTCH = 'Dutch',
  GERMAN = 'German',
  JAPANESE = 'Japanese',
}

export interface AiVoice {
  name: string;
  gender: string;
  region: string;
  value: string;
  languages?: VoiceLanguage[];
  previewAudioUrl?: string;
  recommended?: boolean;
}

export interface AiVoiceFamily {
  name: string;
  description_key: string;
  voices: AiVoice[];
  provider: VendorModel;
  recommended?: boolean;
}

export interface MeetingType {
  name: string;
  meetingTypeId: string;
  calendarId: string;
}

export interface Association {
  name: string;
  meetingTypeId: string;
  calendarId: string;
}

export enum CalendarType {
  INVALID = 'INVALID',
  PERSONAL = 'PERSONAL',
  GROUP = 'GROUP',
  SERVICE = 'SERVICE',
}

export interface CalendarWithMeetingTypes {
  calendarName: string;
  calendarId: string;
  isPersonal: boolean;
  meetingTypes: MeetingType[];
}

export interface Calendar {
  name: string;
  id: string;
  slug?: string;
  icon: string;
  calendarType: CalendarType;
  associations?: Association[];
}

export interface ProductIcon {
  iconUrl?: string;
  matIcon?: string;
  productName: string;
}

export type CapabilityProductMap = Record<string, ProductIcon>;

export type AssistantTypeProductMap = Partial<Record<AssistantType, ProductIcon>>;

// Combined assistant product map for white-labeled products
export interface AssistantProductMap {
  assistantTypeMap?: AssistantTypeProductMap;
  capabilityIdMap?: CapabilityProductMap;
}
